/**
 * 参数值的浮点语义。
 *
 * 一条必须记住的协议事实：MAVLink 的 `PARAM_VALUE.param_value` 与
 * `PARAM_SET.param_value` 都是 **float32**，而 JS 的 number 是 double。
 * 于是"写入 0.1，回读 0.10000000149011612"是**完全正常**的现象 ——
 * 用 `===` 比较会把每一次成功的写入都判成失败。而"明明写进去了却报失败"
 * 会让操作员反复重试、甚至去改别的参数来"绕过"，是地面站里最伤信任的一类 bug。
 *
 * 判定分两级，从强到弱：
 *   1. 两边 `Math.fround()` 后相等 —— 这是线上格式能表达的最小差异，最强判据；
 *   2. 落差在容差内 —— 飞控内部按 `AP_Int8/16/32` 存储（`__int8` / `__int16` 之类的
 *      `var_info` 类型）或对值做了取整时，回读值本就不会与写入值逐位相同。
 *
 * 容差取 1e-6 绝对 + 1e-6 相对：对"微调量"（1e-3 量级）是 1e-9 的精度，
 * 对"航点速度"（500 量级）是 5e-4 的精度，都远小于任何有物理意义的参数步长，
 * 却足以吸收 float32 在多次运算后的相对误差（约 1e-7）。
 */

/** 绝对容差 */
export const PARAM_ABS_TOLERANCE = 1e-6;

/** 相对容差（乘以两值中较大的那个的绝对值） */
export const PARAM_REL_TOLERANCE = 1e-6;

/** 把值折叠到 float32。用于"线上到底能不能区分这两个数"的判断。 */
export function toFloat32(value: number): number {
  return Math.fround(value);
}

/**
 * 两个参数值是否可视为同一个值。
 *
 * NaN 与自身视为相同（参数值不该是 NaN，但真的收到时应判为"回读一致"，
 * 而不是把一条 NaN 永远当成写入失败）；±0 视为相同。
 */
export function paramValuesEqual(a: number, b: number): boolean {
  if (Number.isNaN(a) || Number.isNaN(b)) return Number.isNaN(a) && Number.isNaN(b);
  if (a === b) return true; // 同时覆盖 ±0 与 ±Infinity
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  // float32 意义上的相同：0.1 与 float32(0.1) 走的就是这条路
  if (Math.fround(a) === Math.fround(b)) return true;
  const diff = Math.abs(a - b);
  const scale = Math.max(Math.abs(a), Math.abs(b));
  return diff <= Math.max(PARAM_ABS_TOLERANCE, PARAM_REL_TOLERANCE * scale);
}

/** 有限数检查（NaN / ±Infinity 都不是合法的参数值） */
export function isFiniteParamValue(value: number): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * 把参数值写成文本，供导出 `.param` / `.params` 文件用。
 *
 * **输出"能表达这个 float32 的最短十进制"**，而不是 `String(value)`：
 * 从机体读回来的值（float32 的 0.1）在 JS 里是 0.10000000149011612，
 * 直接 `String()` 会写出 17 位有效数字 —— 虽然能被 Mission Planner / QGC 读回，
 * 但它不像人写出来的文件，肉眼 diff 参数备份时会把每一行都标成"变了"。
 * Mission Planner（C# 的 `float.ToString`）与 QGC 输出的都是 `0.1`，这里与它们对齐：
 * 逐位提高精度，取第一个"再解析回 float32 后与原值相同"的表示。
 */
export function formatParamValue(value: number): string {
  if (!Number.isFinite(value)) {
    throw new RangeError(`参数值必须是有限数，收到 ${String(value)}`);
  }
  // `-0` 写成 `-0` 会被某些解析器当成非法，且它与 0 在任何参数上都无区别
  if (Object.is(value, -0)) return '0';
  // 整数直接输出：SERIAL1_BAUD=57600 写成 5.76e+4 虽然合法，但对人不友好
  if (Number.isInteger(value) && Math.abs(value) < 1e15) return String(value);

  const target = Math.fround(value);
  for (let precision = 1; precision <= 9; precision += 1) {
    const text = value.toPrecision(precision);
    // `Number(text)` 再去掉 toPrecision 补出来的尾零（"0.10" → "0.1"）
    if (Math.fround(Number(text)) === target) return String(Number(text));
  }
  return String(value);
}
